import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import rolesApi from "../api/rolesApi";
import RequirePermission from "../auth/RequirePermission";

/**
 * Lists all roles with their user/permission counts. Entry point for the
 * RBAC admin area. Create/Edit/Delete actions are each gated behind the
 * matching permission (server enforces too).
 *
 * System roles (IsSystem) cannot be deleted — the delete button is replaced
 * with a tooltip explaining why, matching the server's SYSTEM_ROLE_PROTECTED.
 */
export default function RolesListPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  const showToast = (msg, tone = "info") => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchRoles = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await rolesApi.list();
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (role) => {
    if (role.isSystem) return; // guarded in UI; server also blocks
    const confirmed = window.confirm(
      `Delete role "${role.name}"? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(role.id);
    setError("");
    try {
      await rolesApi.remove(role.id);
      showToast(`Role "${role.name}" deleted`);
      fetchRoles();
    } catch (err) {
      const code = err?.response?.data?.errorCode;
      const msg =
        code === "ROLE_IN_USE"
          ? "This role is assigned to users. Reassign them first."
          : code === "SYSTEM_ROLE_PROTECTED"
          ? "System roles cannot be deleted."
          : err?.response?.data?.message || "Failed to delete role";
      setError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="shell">
      <header className="hero">
        <div>
          <p className="eyebrow">Access Control</p>
          <h1>Roles & Permissions</h1>
          <p className="lede">
            Define roles and the permissions they grant. System roles are
            protected and can't be deleted.
          </p>
        </div>

        <div className="hero-right">
          <div className="actions">
            <button className="ghost" onClick={() => navigate("/")}>
              Dashboard
            </button>
            <button className="ghost" onClick={fetchRoles} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <RequirePermission code="roles.create">
              <button className="primary" onClick={() => navigate("/roles/new")}>
                New Role
              </button>
            </RequirePermission>
          </div>
        </div>
      </header>

      {toast && <div className={`toast ${toast.tone}`}>{toast.msg}</div>}
      {error && <div className="toast error">{error}</div>}

      <section className="table-wrap">
        <div className="table roles">
          <div className="row head">
            <span>Name</span>
            <span>Description</span>
            <span>Users</span>
            <span>Permissions</span>
            <span>Type</span>
            <span style={{ textAlign: "right" }}>Actions</span>
          </div>

          {roles.map((role) => (
            <div className="row" key={role.id}>
              <span style={{ fontWeight: 600 }}>{role.name}</span>
              <span title={role.description || ""} className="hint">
                {role.description || "—"}
              </span>
              <span>{role.userCount}</span>
              <span>{role.permissionCount}</span>
              <span>
                <span className={`badge ${role.isSystem ? "neutral" : "success"}`}>
                  {role.isSystem ? "System" : "Custom"}
                </span>
              </span>
              <span style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <RequirePermission anyOf={["roles.update", "roles.manage_permissions", "roles.read"]}>
                  <button
                    className="ghost"
                    onClick={() => navigate(`/roles/${role.id}`)}
                  >
                    Edit
                  </button>
                </RequirePermission>

                <RequirePermission code="roles.delete">
                  {role.isSystem ? (
                    <button
                      className="ghost"
                      disabled
                      title="System roles cannot be deleted"
                      style={{ opacity: 0.4, cursor: "not-allowed" }}
                    >
                      Delete
                    </button>
                  ) : (
                    <button
                      className="ghost danger"
                      onClick={() => handleDelete(role)}
                      disabled={deletingId === role.id}
                    >
                      {deletingId === role.id ? "Deleting..." : "Delete"}
                    </button>
                  )}
                </RequirePermission>
              </span>
            </div>
          ))}

          {!loading && !roles.length && (
            <div className="row empty">No roles found</div>
          )}
        </div>
      </section>
    </div>
  );
}